import {
  type ApiClientOptions,
  type CursorPagedResponse,
  ApiClient,
  type XhrSendRequestOptions,
} from '@/infra/api-client'
import type {
  CreateDocumentRequest,
  DocumentSignedDownloadUrlResource,
  DocumentPopulateParams,
  DocumentResource,
  ListDocumentsRequest,
  UpdateDocumentRequest,
} from '@/features/docs/api/resources/docs'
import type {
  CreateDocumentVersionRequest,
  DocumentVersionResource,
} from '@/features/docs/api/resources/doc-versions'
import type {
  DocumentTypeResource,
  ListDocumentTypesRequest,
} from '@/features/docs/api/resources/doc-types'
import type { DocsRealtimeMessage } from '@/features/docs/api/resources/docs-realtime'
import type {
  CreateDocumentAccessPayload,
  DocumentAccessResource,
  UpdateDocumentAccessPayload,
} from '@/features/docs/api/resources/doc-accesses'
import { toURLSearchParams } from '@/utils/url-util'
import { type Observable } from 'rxjs'

/**
 * Documents API root.
 */
export class DocsApi {
  readonly documents
  readonly versions
  readonly types
  readonly accesses

  constructor(options: ApiClientOptions) {
    this.documents = new DocumentApi(options)
    this.versions = new DocumentVersionApi(options)
    this.types = new DocumentTypeApi(options)
    this.accesses = new DocumentAccessApi(options)
  }
}

/**
 * API for the document resource.
 */
export class DocumentApi extends ApiClient {
  /**
   * Real-time events for all documents-related stuff.
   */
  events(): Observable<DocsRealtimeMessage> {
    return this._events()
  }

  /**
   * Lists documents.
   */
  list(
    request: ListDocumentsRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<DocumentResource>> {
    const params = toURLSearchParams(
      { ...request },
      { arrayStyle: 'multi-key-bracket' },
    )
    return this._get(`v3/documents`, params, { signal })
  }

  /**
   * Creates a new document.
   */
  create(
    request: CreateDocumentRequest,
    params?: DocumentPopulateParams,
  ): Promise<DocumentResource> {
    const urlParams = toURLSearchParams({ ...params })
    return this._post(`v3/documents`, request, urlParams)
  }

  /**
   * Gets a document by ID.
   */
  get(id: string, params?: DocumentPopulateParams): Promise<DocumentResource> {
    return this._get(`v3/documents/${id}`, toURLSearchParams({ ...params }))
  }

  /**
   * Updates a document.
   */
  patch(
    id: string,
    request: UpdateDocumentRequest,
    params?: DocumentPopulateParams,
  ): Promise<DocumentResource> {
    const urlParams = toURLSearchParams({ ...params })
    return this._patch(`v3/documents/${id}`, request, urlParams)
  }

  /**
   * Archives a document.
   */
  archive(id: string): Promise<void> {
    return this._patch(`v3/documents/${id}/archive`, undefined)
  }

  /**
   * Gets the signed download URL for the document.
   * @param id
   * @param preview
   */
  getSignedDownloadUrl(
    id: string,
    preview?: boolean,
  ): Promise<DocumentSignedDownloadUrlResource> {
    const params = new URLSearchParams()
    if (preview !== undefined) {
      params.append('preview', preview.toString())
    }
    return this._get(`v3/documents/${id}/download`, params)
  }
}

/**
 * API for the document type resource.
 */
export class DocumentTypeApi extends ApiClient {
  /**
   * Lists document types.
   */
  list(
    request: ListDocumentTypesRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<DocumentTypeResource>> {
    const params = toURLSearchParams({ ...request })
    return this._get(`v4/types`, params, { signal })
  }

  /**
   * Gets document types by their IDs.
   *
   * @param ids
   */
  getMany(ids: readonly string[]): Promise<(DocumentTypeResource | null)[]> {
    return this._post(`v3/types/id-query`, { ids })
  }
}

/**
 * API for the document access resource.
 */
export class DocumentAccessApi extends ApiClient {
  /**
   * Lists document accesses for a specific document.
   */
  list(
    documentId: string,
    signal?: AbortSignal,
  ): Promise<DocumentAccessResource[]> {
    return this._get(`v3/documents/${documentId}/accesses`, undefined, {
      signal,
    })
  }

  /**
   * Gets a document access by ID.
   */
  get(id: string): Promise<DocumentAccessResource> {
    return this._get(`v3/accesses/${id}`)
  }

  /**
   * Creates a new document access.
   */
  create(
    payload: CreateDocumentAccessPayload,
  ): Promise<DocumentAccessResource> {
    return this._post(`v3/accesses`, payload)
  }

  /**
   * Updates a document access.
   */
  patch(
    id: string,
    payload: UpdateDocumentAccessPayload,
  ): Promise<DocumentAccessResource> {
    return this._patch(`v3/accesses/${id}`, payload)
  }

  /**
   * Removes a document access.
   */
  remove(id: string): Promise<void> {
    return this._delete(`v3/accesses/${id}`)
  }
}

/**
 * Options for {@link DocumentVersionApi.create}.
 */
export interface CreateDocumentVersionOptions {
  /**
   * Abort signal for uploading the document.
   */
  signal?: AbortSignal

  /**
   * Callback for progress events.
   *
   * @param loaded How much has been uploaded.
   * @param total The total size to upload.
   */
  onProgress?: (loaded: number, total: number) => unknown
}

/**
 * API for the document version resource.
 */
export class DocumentVersionApi extends ApiClient {
  /**
   * Creates a new document version.
   */
  async create(
    { document_id: id, ...request }: CreateDocumentVersionRequest,
    options?: CreateDocumentVersionOptions,
  ): Promise<DocumentVersionResource> {
    if (request.action.type === 'RevertToVersion') {
      return this._post(`v3/documents/${id}/versions`, request, undefined, {
        signal: options?.signal,
      })
    }

    const body = new FormData()
    const { files, ...action } = request.action
    body.append('meta', JSON.stringify({ action, message: request.message }))
    files.forEach((f, i) =>
      body.append(`file${i + 1}`, f, sanitizeFileName(f.name)),
    )

    const url = new URL(`v3/documents/${id}/versions`, this.options.baseURL)

    const req = new Request(url, {
      method: 'POST',
      signal: options?.signal,
    })

    const xhrOpts: XhrSendRequestOptions = {
      form: body,
      onProgress: (e) => options?.onProgress?.(e.loaded, e.total),
    }

    return this._xhr(req, xhrOpts)
      .then(ApiClient.throwIfNotSuccessful)
      .then((r) => r.json() as Promise<DocumentVersionResource>)
  }
}

/**
 * Sanitizes a file name.
 * @param {string} str
 */
function sanitizeFileName(str: string) {
  return str.replace(/\//g, '_').replace(/\\/g, '_')
}
